import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Mic, Video } from 'lucide-react';
import { useHeyGenToken } from '@/hooks/useHeyGenToken';
import { ELEVEN_LABS_OCTAVE_AGENT_ID } from '@/utils/constants';
import api from '@/utils/ky';

interface ModeSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectMode: (mode: 'text' | 'voice' | 'video') => void;
  moduleId?: string;
}

export function ModeSelectionDialog({
  isOpen,
  onClose,
  onSelectMode,
  moduleId = 'general-coaching',
}: ModeSelectionDialogProps) {
  // Initialize the HeyGen token hook
  const { 
    token: heyGenToken, 
    loading: heyGenLoading, 
    error: heyGenError, 
    generateToken, 
    clearToken 
  } = useHeyGenToken();
  
  // We'll no longer pre-fetch 11Labs session

  // Pre-fetch tokens and configs when dialog opens
  useEffect(() => {
    if (isOpen) {
      // Only pre-fetch HeyGen token now
      console.log('[ModeSelectionDialog] Pre-fetching HeyGen token for video mode...');

      // Start HeyGen token generation (for video)
      generateToken();
    }
  }, [isOpen, generateToken, moduleId]);

  // Handler for mode selection with proper cleanup
  const handleSelectMode = (mode: 'text' | 'voice' | 'video') => {
    if (mode === 'video') {
      console.log('[ModeSelectionDialog] Keeping HeyGen token for video mode');
    } else {
      // For voice or text mode, clear HeyGen token
      console.log('[ModeSelectionDialog] Clearing HeyGen token');
      clearToken();
    }
    
    // Call the parent handler with selected mode
    onSelectMode(mode);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-lg transition-all border border-gray-200">
                <div className="mb-4">
                  <Dialog.Title as="h3" className="text-2xl font-bold text-gray-900">
                    Choose interaction mode
                  </Dialog.Title>
                  <p className="text-sm text-gray-600 mt-2">
                    Select how you'd like to interact with your coach
                  </p>
                </div>
                
                <div className="mt-6 space-y-4">
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleSelectMode('text')}
                    className="flex items-center p-4 bg-white border border-gray-200 rounded-lg cursor-pointer hover:border-primary/30 hover:shadow-md transition-all group"
                  >
                    <div className="bg-primary/10 p-3 rounded-lg">
                      <MessageSquare className="h-5 w-5 text-primary" />
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-900 group-hover:text-primary transition-colors">Text Chat</h4>
                      <p className="text-sm text-gray-600 mt-0.5">Interact through text messages</p>
                    </div>
                  </motion.div>

                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleSelectMode('voice')}
                    className="flex items-center p-4 bg-white border border-gray-200 rounded-lg cursor-pointer hover:border-primary/30 hover:shadow-md transition-all group"
                  >
                    <div className="bg-primary/10 p-3 rounded-lg">
                      <Mic className="h-5 w-5 text-primary" />
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-900 group-hover:text-primary transition-colors">Voice Call</h4>
                      <p className="text-sm text-gray-600 mt-0.5">Interact through voice conversation</p>
                    </div>
                  </motion.div>

                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleSelectMode('video')}
                    className="flex items-center p-4 bg-white border border-gray-200 rounded-lg cursor-pointer hover:border-primary/30 hover:shadow-md transition-all group"
                  >
                    <div className="bg-primary/10 p-3 rounded-lg">
                      <Video className="h-5 w-5 text-primary" />
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-900 group-hover:text-primary transition-colors">Video Call</h4>
                      <p className="text-sm text-gray-600 mt-0.5">Interact through video conversation</p>
                    </div>
                  </motion.div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/20 transition-colors"
                    onClick={() => {
                      // Clean up resources if user cancels
                      clearToken();
                      onClose();
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}